import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { PaymentState } from "../../../constants";
import { objectKeys } from "../../../utils/functional";
import { IReduxState } from "../reducers.interfaces";
import { serializeCheckoutError } from "../utils";
import {
    PAYMENT_REJECT_MSGS,
    buildCheckoutError,
    findDeclinedMethodType,
    forwardToThankYouPage,
    isOrderCompleteOrForwardable,
} from "./paymentStates";

/**
 * Complete order payment after initial order placement.
 *
 * Checks the payment states of an order that placeOrder or
 * completeDeferredPayment just submitted. It never creates an order.
 *
 * @throws {ICheckoutHTTPError} If payment is declined or left pending
 *
 * @sideEffects
 * - Makes API call to check payment states via checkoutAPI.checkPaymentStates
 * - Updates `data.payment_states` via updatePaymentStates action
 * - May redirect to thank you page via forwardToThankYouPage
 */
export const completeOrderPayment = createAsyncThunk(
    "checkout/completeOrderPayment",
    async (_: void, { getState, dispatch }): Promise<void> => {
        const state = getState() as { checkout: IReduxState };
        const checkoutState = state.checkout;

        const states = await checkoutAPI.checkPaymentStates();

        // Import actions dynamically to avoid circular dependency
        const { updatePaymentStates } = await import("../reducers");
        // Write the states to redux on every outcome,
        // we were losing the GA purchase events (#35199).
        dispatch(updatePaymentStates(states));

        if (isOrderCompleteOrForwardable(states)) {
            forwardToThankYouPage();
            return;
        }

        // Check for decline
        const declinedMethodType = findDeclinedMethodType(
            states,
            checkoutState.form.payment_methods,
        );
        if (declinedMethodType) {
            throw buildCheckoutError(
                "global",
                PAYMENT_REJECT_MSGS[declinedMethodType],
            );
        }

        const pendingKey = objectKeys(states.payment_method_states).find(
            (key) =>
                states.payment_method_states[key].status ===
                PaymentState.PENDING,
        );
        if (pendingKey) {
            throw buildCheckoutError(
                "payment",
                `${checkoutState.form.payment_methods[pendingKey].method_type} payment should never be pending`,
            );
        }
    },
    { serializeError: serializeCheckoutError },
);
